import { useMemo } from "react";
import { useGetFarmGateProcessDataById } from "../../../../../api/cultivations";
import { useGetInHouseIngredient } from "../../../../../api/ingredients";
import { useGetProcessGateProcessDataById } from "../../../../../api/process";
import { ApiProcessDetail } from "../../../../../types/prodcut-data-review/product-data.types";
import { GateKind } from "../types/processDetail.types";
import { buildLookupMap, mapProcessDetail } from "../utils/processDetail.utils";

interface UseProcessDetailParams {
  processId: string;
  gateKind: GateKind;
  /** Only fetch once the row is expanded. */
  enabled: boolean;
}

/**
 * Loads the full per-process record (chemicals, waste/side-stream, CIP/SIP) from
 * the same detail endpoints the mapping-tool Processing drawers use, instead of
 * relying on the bulk mapping-all-data payload which is missing/mis-shaped for
 * these fields (see ApiProcessingRecord doc comment).
 */
export const useProcessDetail = ({ processId, gateKind, enabled }: UseProcessDetailParams) => {
  const farmGateQuery = useGetFarmGateProcessDataById(gateKind === "farm" && enabled ? processId : undefined);
  const processGateQuery = useGetProcessGateProcessDataById(
    gateKind === "process" && enabled ? processId : undefined
  );

  const { data: inHouseIngredients } = useGetInHouseIngredient();

  const activeQuery = gateKind === "farm" ? farmGateQuery : processGateQuery;
  const detail = activeQuery.data as ApiProcessDetail | undefined;

  const lookups = useMemo(
    () => ({ ingredientNameMap: buildLookupMap(inHouseIngredients) }),
    [inHouseIngredients]
  );

  const viewModel = useMemo(() => mapProcessDetail(detail, lookups), [detail, lookups]);

  return {
    detail,
    ...viewModel,
    lookups,
    isLoading: activeQuery.isLoading,
    isError: activeQuery.isError,
    refetch: activeQuery.refetch
  };
};
